import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  WifiOutlined,
  WifiOffOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import type { PlanEntity, RunEntity } from '@/types/run-stage.types';
import { useRunStageStore } from '@/run-stage/store/runStageStore';
import {
  updateRunTimings,
  clonePlanGraph,
  executeInitV2,
} from '@services/runApi.service';
import { setExecutionMode } from '@services/runStageApi.service';

interface ExecutionModeSelectorProps {
  plan: PlanEntity;
  run: RunEntity;
  isReadOnly?: boolean;
  onExecutionStarted: (mode: 'Online' | 'Offline') => void;
}

export const ExecutionModeSelector: React.FC<ExecutionModeSelectorProps> = ({
  plan,
  run,
  isReadOnly = false,
  onExecutionStarted,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmMode, setConfirmMode] = useState<'Online' | 'Offline' | null>(null);
  const refresh = useRunStageStore((s) => s.refresh);

  const runId = run._id;
  const runUid = run.uid;
  const entityType =
    typeof run.version === 'object' ? run.version?.experiment?.fileType : undefined;

  const modeAlreadySelected = !!plan.typeOfExecution;

  const buildDurationFromPlanStart = (startTime?: string | null): string | undefined => {
    if (!startTime) return undefined;
    const startDate = new Date(startTime);
    if (Number.isNaN(startDate.getTime())) return undefined;

    let seconds = Math.max(0, Math.floor((Date.now() - startDate.getTime()) / 1000));
    const days = Math.floor(seconds / (3600 * 24));
    seconds -= days * 3600 * 24;
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    return `${days} days, ${hours} Hrs, ${minutes} Minutes, ${seconds.toFixed(2)} Seconds`;
  };

  /** Online execution: mirrors Angular displayExecute() */
  const startOnlineExecution = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // 1. Set typeOfExecution = 'Online' on plan
      await setExecutionMode({ runId, runUid, typeOfExecution: 'Online' });

      // 2. Clone plan graph if not already cloned
      if (!run.isCloned) {
        await clonePlanGraph({ runId, runUid, entityType });
      }

      // 2b. Initialize v2 execute (set executeOrder/executeStatus on vertices)
      const isV2 = Boolean(run.operationsInNodes) || run.sourceEditor === 'maxgraph';
      if (isV2) {
        await executeInitV2({ runId });
      }

      // 3. Update run timings (startTime + status = LIVE)
      await updateRunTimings({
        _id: runId,
        uid: runUid,
        startTime: new Date().toISOString(),
        status: 'LIVE',
        entityType,
      });

      // 4. Refresh all state
      await refresh();

      onExecutionStarted('Online');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  /** Offline execution */
  const startOfflineExecution = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const nowIso = new Date().toISOString();
      const duration = buildDurationFromPlanStart(plan.startTime ?? null);

      // 1. Set typeOfExecution = 'Offline' on plan
      await setExecutionMode({
        runId,
        runUid,
        typeOfExecution: 'Offline',
        endTime: nowIso,
        ...(duration ? { duration } : {}),
      });

      // 2. Clone plan graph if not already cloned
      if (!run.isCloned) {
        await clonePlanGraph({ runId, runUid, entityType });
      }

      // 2b. Initialize v2 execute (set executeOrder/executeStatus on vertices)
      const isV2 = Boolean(run.operationsInNodes) || run.sourceEditor === 'maxgraph';
      if (isV2) {
        await executeInitV2({ runId });
      }

      // 3. Refresh all state
      await refresh();

      onExecutionStarted('Offline');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirm = async () => {
    const mode = confirmMode; // Capture before clearing
    setConfirmMode(null);
    
    if (mode === 'Online') {
      await startOnlineExecution();
    } else if (mode === 'Offline') {
      await startOfflineExecution();
    }
  };

  // If mode already selected, show current mode and redirect
  if (modeAlreadySelected) {
    const mode = plan.typeOfExecution;
    return (
      <Box sx={{ p: 3, maxWidth: 600 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Execution mode is set to <strong>{mode}</strong>. Proceeding to execution.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Select Execution Mode
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how you want to execute this run. Once selected, this option cannot be changed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isSaving && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Stack direction="row" spacing={3}>
        {/* Online card */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            p: 3,
            cursor: isReadOnly || isSaving ? 'default' : 'pointer',
            borderColor: 'divider',
            transition: 'all 0.15s',
            '&:hover': isReadOnly || isSaving ? {} : { borderColor: 'primary.main', boxShadow: 1 },
          }}
          onClick={() => !isReadOnly && !isSaving && setConfirmMode('Online')}
        >
          <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
            <WifiOutlined color="primary" sx={{ fontSize: 48 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Online Execution
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Execute with a device like a laptop or tablet. Step start and end times are
              automatically recorded by the system in real time.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={isReadOnly || isSaving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmMode('Online');
              }}
            >
              Select Online
            </Button>
          </Stack>
        </Paper>

        {/* Offline card */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            p: 3,
            cursor: isReadOnly || isSaving ? 'default' : 'pointer',
            borderColor: 'divider',
            transition: 'all 0.15s',
            '&:hover': isReadOnly || isSaving ? {} : { borderColor: 'primary.main', boxShadow: 1 },
          }}
          onClick={() => !isReadOnly && !isSaving && setConfirmMode('Offline')}
        >
          <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
            <WifiOffOutlined color="action" sx={{ fontSize: 48 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Offline Execution
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Execute without an internet-connected device. You can update step start and end
              times manually after execution.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={isReadOnly || isSaving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmMode('Offline');
              }}
            >
              Select Offline
            </Button>
          </Stack>
        </Paper>
      </Stack>

      {/* Confirmation dialog */}
      <Dialog open={confirmMode !== null} onClose={() => setConfirmMode(null)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlined color="warning" />
          Confirmation Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Please make sure you have selected all the correct materials and instruments.
            Once selected, this option cannot be changed. Do you wish to continue with{' '}
            <strong>{confirmMode}</strong> execution?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMode(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} autoFocus>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
